/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import Debounce from "lodash/debounce"; // Npm: lodash library.
import _ from "underscore"; // Npm: utility module.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import SenderID from "components/SenderID";
import VendorAccountUpsert from "components/VendorAccountUpsert";
import VendorAccountDelete from "components/VendorAccountDelete";
import AutoRateImport from "components/AutoRateImport";
import VendorAccountDropDownOptions from "components/VendorAccountDropDownOptions";
import { MemoizedInput } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import VendorAccountReadQuery from "./__query__/index.vendorAccount.read.query";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [vendorNameToSearch, setVendorNameToSearch] = React.useState("");
  const [vendorAccountNameToSearch, setVendorAccountNameToSearch] =
    React.useState("");
  const [rateNameToSearch, setRateNameToSearch] = React.useState("");
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    // 'logicOperator': 'and',
    // 'quickFilterValues': [],
    // 'quickFilterLogicOperator': 'and'
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const {
    onOpen: onVendorAccountCreateOpen,
    isOpen: isVendorAccountCreateOpen,
    onClose: onVendorAccountCreateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorAccountUpdateOpen,
    isOpen: isVendorAccountUpdateOpen,
    onClose: onVendorAccountUpdateClose,
  } = useDisclosure();
  const {
    onOpen: onVendorAccountDeleteOpen,
    isOpen: isVendorAccountDeleteOpen,
    onClose: onVendorAccountDeleteClose,
  } = useDisclosure();
  const {
    onOpen: onSenderIDAddOpen,
    isOpen: isSenderIDAddOpen,
    onClose: onSenderIDAddClose,
  } = useDisclosure();
  const {
    onOpen: onAutoRateImportOpen,
    isOpen: isAutoRateImportOpen,
    onClose: onAutoRateImportClose,
  } = useDisclosure();

  // Data assignment.
  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = { ...current, page: 0 };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);

  const _QueryVendorAccountReadVariables = React.useMemo(() => ({
    vendorId: passOn?.vendorId,
    vendorNameToSearch,
    vendorAccountNameToSearch,
    rateNameToSearch,
    skip: paginationModel.pageSize * paginationModel.page,
    take: paginationModel.pageSize,
    filter: _appliedFilter,
  }), [_appliedFilter, paginationModel.page, paginationModel.pageSize, passOn?.vendorId, rateNameToSearch, vendorAccountNameToSearch, vendorNameToSearch]);

  const _QueryVendorAccountRead = useQuery(VendorAccountReadQuery, {
    variables: _QueryVendorAccountReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });
  const _onVendorNameToSearchInputChange = React.useRef(
    Debounce((e) => {
      _ResetPagination();
      setVendorNameToSearch(e.target.value);
    }, 1000),
  ).current;
  const _onVendorAccountNameToSearchInputChange = React.useRef(
    Debounce((e) => {
      _ResetPagination();
      setVendorAccountNameToSearch(e.target.value);
    }, 1000),
  ).current;
  const _onRateNameToSearchInputChange = React.useRef(
    Debounce((e) => {
      _ResetPagination();
      setRateNameToSearch(e.target.value);
    }, 1000),
  ).current;

  React.useEffect(() => {
    return () => {
      _setFilterDebounced.cancel?.();
      _onVendorNameToSearchInputChange.cancel?.();
      _onVendorAccountNameToSearchInputChange.cancel?.();
      _onRateNameToSearchInputChange.cancel?.();
    };
  }, []);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isFirstLoadCompleted = React.useRef(false);
  const _isCurrentViewMobile = useBreakpointValue({
    base: "false",
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Data assignment.
  if (
    !_QueryVendorAccountRead.loading &&
    0 < _QueryVendorAccountRead?.data?.VendorAccountRead?.length
  )
    _isFirstLoadCompleted.current = true;
  if (
    0 === _QueryVendorAccountRead?.data?.VendorAccountRead?.length ||
    !_.every(
      _.pluck(_QueryVendorAccountRead?.data?.VendorAccountRead, "status"),
      (j) => _successFlags.includes(j),
    )
  )
    _isFirstLoadCompleted.current = false;

  // Columns definition.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        width: 80,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "id",
        headerName: "ID",
        hide: true,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: "displayName",
        headerName: "Name",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: "Vendor.displayName",
        headerName: "Vendor",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: true,
      },
      {
        field: "Rate.displayName",
        headerName: "Rate",
        flex: 1,
        minWidth: 150,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: true,
      },
      {
        field: "Vendor.Billing.currency",
        headerName: "Currency",
        width: 120,
        align: "center",
        headerAlign: "center",
        editable: false,
        sortable: true,
        filterable: false,
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
            }}
          >
            {value ? (
              <Text
                borderRadius="10px"
                padding="5px 15px"
                lineHeight="18px"
                color="rgba(117, 81, 255, 1)"
                bg="rgba(117, 81, 255, .1)"
              >
                Active
              </Text>
            ) : (
              <Text
                borderRadius="10px"
                padding="5px 15px"
                lineHeight="18px"
                color="rgba(238, 93, 80, 1)"
                bg="rgba(255, 242, 242, 1)"
              >
                In Active
              </Text>
            )}
          </Box>
        ),
      },

      {
        field: "createdAt",
        headerName: "CreatedAt",
        width: 150,
        align: "center",
        headerAlign: "center",
        type: "date",
        editable: false,
        sortable: true,
        filterable: false,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        width: 150,
        align: "center",
        headerAlign: "center",
        type: "date",
        editable: false,
        sortable: true,
        filterable: false,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorAccountDropDownOptions
            vendorAccountId={row.id}
            smppId={row.smppId}
            onVendorAccountUpdate={onVendorAccountUpdateOpen}
            onVendorAccountDelete={onVendorAccountDeleteOpen}
            onAutoRateImport={onAutoRateImportOpen}
            onAddSenderID={onSenderIDAddOpen}
          />
        ),
      },
    ],
    [
      onVendorAccountUpdateOpen,
      onVendorAccountDeleteOpen,
      onAutoRateImportOpen,
      onSenderIDAddOpen,
    ],
  );

  // Rows definition.
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryVendorAccountRead?.data?.VendorAccountRead?.map((item, index) =>
          _successFlags.includes(item.status)
            ? {
                id:
                  item?.id ??
                  `vendor-account-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                displayName: item?.displayName ?? "-",
                "Vendor.displayName": item?.Vendor?.displayName ?? "-",
                "Rate.displayName": item?.Rate?.displayName ?? "-",
                "Vendor.Billing.currency":
                  item?.Vendor?.Billing?.currency ?? "-",
                status: item?.Smpp?.isActive || false,
                createdAt: new Date(item?.createdAt),
                updatedAt: new Date(item?.updatedAt),
                smppId: item?.Smpp?.id,
              }
            : null,
        ),
      ),
    [
      _QueryVendorAccountRead?.data?.VendorAccountRead,
      paginationModel.page,
      paginationModel.pageSize,
      _successFlags,
    ],
  );
  const _QueryVendorAccountReadRowCount = _.first(_QueryVendorAccountRead?.data?.VendorAccountRead)?._totalCount;
  const _rowCount = React.useMemo(() => {
    if (undefined !== _QueryVendorAccountReadRowCount && null !== _QueryVendorAccountReadRowCount)
      _rowCountRef.current = _QueryVendorAccountReadRowCount;

    return _rowCountRef.current;
  }, [_QueryVendorAccountReadRowCount]);
  const _downloadCellsData = React.useMemo(
    () =>
      rows?.map((item) => ({
        "S.No.": item.sno,
        Name: item.displayName,
        Vendor: item.vendorName,
        Rate: item.rateName,
        Currency: item.currency,
        Status: item.status ? "Active" : "In Active",
        CreatedAt: Moment(item.createdAt).format("YYYY-MM-DD"),
        UpdatedAt: Moment(item.updatedAt).format("YYYY-MM-DD"),
      })),
    [rows],
  );
  const _downloadHeadersData = React.useMemo(
    () =>
      [
        "S.No.",
        "Name",
        "Vendor",
        "Rate",
        "Currency",
        "Status",
        "CreatedAt",
        "UpdatedAt",
      ].map((i) => ({ key: i, label: i })),
    [],
  );
  const _HandleRefresh = React.useCallback(async () => {
    // Set refreshing state.
    setIsRefreshing(true);

    try {
      // Refetch vendor accounts with current query variables.
      await _QueryVendorAccountRead.refetch();
    } finally {
      // Reset refreshing state.
      setIsRefreshing(false);
    }
  }, [_QueryVendorAccountRead.refetch]);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleFilterModelChange = React.useCallback((__filterModel) => {
    _ResetPagination();
    _setFilterDebounced(__filterModel);
  }, [_ResetPagination, _setFilterDebounced]);

  // Return component.
  return (
    <>
      <Flex className="vendorAccount base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH={_isCurrentViewMobile ? "115vh" : "98vh"}
          maxH={_isCurrentViewMobile ? "115vh" : "98vh"}
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Create new vendor account." fontSize="sm">
              <Button
                onClick={onVendorAccountCreateOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New Account
              </Button>
            </Tooltip>
            <Flex gap="12px" alignItems="center">
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={_HandleRefresh}
                isDisabled={isRefreshing}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={_downloadCellsData}
                headersData={_downloadHeadersData}
              />
            </Flex>
          </Flex>

          {/* Filter */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="16px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
          >
            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
                mb="8px"
              >
                Account Name
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={vendorAccountNameToSearch}
                onChange={_onVendorAccountNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
                mb="8px"
              >
                Vendor Name
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={vendorNameToSearch}
                onChange={_onVendorNameToSearchInputChange}
              />
            </Box>

            <Box
              minW={{ base: "100%", md: "150px" }}
              w={{ base: "100%", md: "200px" }}
            >
              <Text
                fontSize="14px"
                fontWeight={600}
                color="rgb(141, 151, 181)"
                mb="8px"
              >
                Rate Name
              </Text>
              <MemoizedInput
                placeholder="Search"
                className="filter searchInput"
                defaultValue={rateNameToSearch}
                onChange={_onRateNameToSearchInputChange}
              />
            </Box>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={_rowCount}
            paginationMode="server"
            filterMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            loading={
              (_QueryVendorAccountRead?.loading &&
                !_isFirstLoadCompleted.current) ||
              isRefreshing
            }
          />
        </Flex>
        <Modal
          size="2xl"
          title="Vendor Account Create"
          isOpen={isVendorAccountCreateOpen}
          onClose={onVendorAccountCreateClose}
          isCentered={false}
        >
          <VendorAccountUpsert
            isCreateOnly={true}
            isOpen={isVendorAccountCreateOpen}
            onClose={onVendorAccountCreateClose}
            refetch={_QueryVendorAccountRead.refetch}
          />
        </Modal>
        <Modal
          size="2xl"
          title="Vendor Account Update"
          isOpen={isVendorAccountUpdateOpen}
          onClose={onVendorAccountUpdateClose}
          isCentered={false}
        >
          <VendorAccountUpsert
            isOpen={isVendorAccountUpdateOpen}
            onClose={onVendorAccountUpdateClose}
            refetch={_QueryVendorAccountRead.refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Add Sender ID"
          isOpen={isSenderIDAddOpen}
          onClose={onSenderIDAddClose}
        >
          <SenderID />
        </Modal>
        <Modal
          size="md"
          title="Vendor Account Delete"
          isOpen={isVendorAccountDeleteOpen}
          onClose={onVendorAccountDeleteClose}
        >
          <VendorAccountDelete
            onClose={onVendorAccountDeleteClose}
            refetch={_QueryVendorAccountRead.refetch}
          />
        </Modal>
        <Modal
          size="2xl"
          title="Auto Rate Import"
          isOpen={isAutoRateImportOpen}
          onClose={onAutoRateImportClose}
        >
          <AutoRateImport />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(React.memo(Index));
